import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { CalendarDays, ArrowRight } from "lucide-react";
import type { ScheduledSession } from "@/content/schedule";
import { useScheduledSessions } from "@/hooks/useScheduledSessions";
import { getCourse } from "@/content/courses";

/**
 * Upcoming sessions for a single course.
 *
 * Sessions come live from the Scheduler API via useScheduledSessions.
 */
export function UpcomingSessions({
  courseSlug,
  heading = "Upcoming sessions",
}: {
  courseSlug: string;
  heading?: string;
}) {
  const { upcoming, loading } = useScheduledSessions();
  const sessions = upcoming.filter((s) => s.courseSlug === courseSlug);

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl text-ink md:text-3xl">{heading}</h2>

        <Link
          to="/schedule/"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          View entire schedule
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading dates…</p>
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/70 bg-background p-7">
          <p className="text-sm text-muted-foreground">
            No dates on the calendar yet. Tell us when you need it and we'll schedule around you.
          </p>
          <Link
            to="/contact/"
            className="mt-4 inline-flex items-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand"
          >
            Request a date
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {sessions.map((s) => (
            <li
              key={s.id}
              className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-background p-6 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)] sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-3">
                <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-ink">
                    <SessionStartLine session={s} />
                  </p>
                </div>
              </div>

              <Link
                to="/start/"
                search={{ plan: "one-course", session: s.id }}
                className="inline-flex shrink-0 items-center justify-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand"
              >
                Register
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Number of weekly sessions. Uses the start/end dates when both exist,
 * otherwise falls back to the course's own session count / duration.
 */
function weekCount(s: ScheduledSession) {
  if (s.meetingDates?.length) return s.meetingDates.length;
  if (!s.endDate) {
    const course = getCourse(s.courseSlug);
    if (course?.sessions?.length) return course.sessions.length;
    const weeks = Number(/(\d+)\s*week/i.exec(course?.duration ?? "")?.[1]);
    return weeks > 0 ? weeks : 1;
  }
  const start = Date.parse(`${s.startDate}T00:00:00Z`);
  const end = Date.parse(`${s.endDate}T00:00:00Z`);
  const weeks = Math.round((end - start) / (7 * 86400000)) + 1;
  return Math.max(1, weeks);
}

/** Scheduler API start/end times are UTC. */
const SOURCE_TZ = "UTC";

function tzOffsetMs(ms: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUTC = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return asUTC - ms;
}

/** Convert a UTC clock time ("10:00 AM" or "14:00") on the session start date to an instant. */
function toInstant(s: ScheduledSession, clock: string | undefined): number | null {
  const m = /(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i.exec(clock ?? "");
  if (!m) return null;
  let hour = Number(m[1]);
  if (m[3]) {
    hour %= 12;
    if (m[3].toUpperCase() === "PM") hour += 12;
  }
  const [y, mo, d] = s.startDate.split("-").map(Number);
  let ms = Date.UTC(y, mo - 1, d, hour, Number(m[2]));
  for (let i = 0; i < 2; i++) {
    ms = Date.UTC(y, mo - 1, d, hour, Number(m[2])) - tzOffsetMs(ms, SOURCE_TZ);
  }
  return ms;
}

function startInstant(s: ScheduledSession) {
  return toInstant(s, s.startTime ?? s.time.split("-")[0]);
}

const timeOpts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", hour12: true };

/** "Starting October 6, 2026 at 11:00 PM IST, 4 weeks in a row" — in the viewer's time zone. */
export function SessionStartLine({ session }: { session: ScheduledSession }) {
  const [local, setLocal] = useState<string | null>(null);
  const weeks = weekCount(session);

  useEffect(() => {
    const ms = startInstant(session);
    if (ms == null) return;
    const d = new Date(ms);
    const date = d.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const time = d.toLocaleTimeString(undefined, { ...timeOpts, timeZoneName: "short" });
    setLocal(`${date} at ${time}`);
  }, [session]);

  // Until the browser's time zone is known, show only the date (never the source zone).
  return (
    <>
      Starting {local ?? formatDate(session.startDate)}
      {weeks > 1 ? `, ${weeks} weeks in a row` : ""}
    </>
  );
}

/** "11:00 PM – 12:00 AM IST" in the viewer's time zone. */
export function SessionTimeRange({ session }: { session: ScheduledSession }) {
  const [local, setLocal] = useState<string | null>(null);
  useEffect(() => {
    const start = startInstant(session);
    if (start == null) return setLocal(session.time);
    const end = toInstant(session, session.endTime ?? session.time.split("-")[1]);
    const s = new Date(start).toLocaleTimeString(undefined, timeOpts);
    const e =
      end != null
        ? new Date(end).toLocaleTimeString(undefined, { ...timeOpts, timeZoneName: "short" })
        : new Date(start).toLocaleTimeString(undefined, { ...timeOpts, timeZoneName: "short" });
    setLocal(end != null ? `${s} – ${e}` : e);
  }, [session]);
  return <>{local ?? ""}</>;
}

export function formatRange(startDate: string, endDate?: string) {
  const start = formatDate(startDate);
  if (!endDate) return start;
  return `${start} – ${formatDate(endDate)}`;
}

export function formatDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
